/**
 * Meditation Context - Domain-Specific State Management
 * 
 * Manages meditation sessions, timers, and practice tracking
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

interface MeditationSession {
  id: string;
  type: string;
  duration: number;
  startTime: Date;
  endTime?: Date;
  completed: boolean;
}

interface MeditationContextState {
  currentSession: MeditationSession | null;
  sessions: MeditationSession[];
  totalMinutes: number;
  streak: number;
}

interface MeditationContextValue extends MeditationContextState {
  startSession: (type: string, duration: number) => void;
  endSession: () => void;
  pauseSession: () => void;
  resumeSession: () => void;
  getStats: () => { total: number; thisWeek: number; thisMonth: number };
}

export const MeditationContext = createContext<MeditationContextValue | undefined>(undefined);

export const MeditationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<MeditationContextState>({
    currentSession: null,
    sessions: [],
    totalMinutes: 0,
    streak: 0
  });

  const startSession = useCallback((type: string, duration: number) => {
    const session: MeditationSession = {
      id: `session_${Date.now()}`,
      type,
      duration,
      startTime: new Date(),
      completed: false
    };

    setState(prev => ({
      ...prev,
      currentSession: session
    }));
  }, []);

  const endSession = useCallback(() => {
    if (!state.currentSession) return;

    const completedSession: MeditationSession = {
      ...state.currentSession,
      endTime: new Date(),
      completed: true
    };

    const actualDuration = completedSession.endTime
      ? (completedSession.endTime.getTime() - completedSession.startTime.getTime()) / 1000 / 60
      : completedSession.duration;

    setState(prev => ({
      ...prev,
      currentSession: null,
      sessions: [...prev.sessions, completedSession],
      totalMinutes: prev.totalMinutes + actualDuration
    }));
  }, [state.currentSession]);

  const pauseSession = useCallback(() => {
    console.log('⏸️ Session paused');
  }, []);

  const resumeSession = useCallback(() => {
    console.log('▶️ Session resumed');
  }, []);

  const getStats = useCallback(() => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const thisWeek = state.sessions
      .filter(s => s.startTime >= weekAgo)
      .reduce((sum, s) => sum + s.duration, 0);

    const thisMonth = state.sessions
      .filter(s => s.startTime >= monthAgo)
      .reduce((sum, s) => sum + s.duration, 0);

    return {
      total: state.totalMinutes,
      thisWeek,
      thisMonth
    };
  }, [state.sessions, state.totalMinutes]);

  const value: MeditationContextValue = {
    ...state,
    startSession,
    endSession,
    pauseSession,
    resumeSession,
    getStats
  };

  return <MeditationContext.Provider value={value}>{children}</MeditationContext.Provider>;
};

export const useMeditation = () => {
  const context = useContext(MeditationContext);
  if (!context) {
    throw new Error('useMeditation must be used within MeditationProvider');
  }
  return context;
};

